import { useEffect, useRef, memo } from 'react';

// 색상 이름 매핑 (RGB 값)
const COLOR_MAP = {
  alert: [1, 0.5, 0],   // 주황/적색 계열
  refer: [1, 1, 0],     // 노랑 계열
};

const DIRECTION_MAP = {
  DOWN: 'down',
  UP: 'up',
  LEFT: 'front',
  RIGHT: 'back',
  BACKWARD: 'right',
  FORWARD: 'left',
};

function ModelViewerCard({
  showAlert = true,
  alertType = 'alert',
  showExclamation = true,
  latestEquipmentOp,
}) {
  const publicUrl = process.env.PUBLIC_URL || '';
  const glbPath = `${publicUrl}/movie/3d(m0922-2).glb`;
  const viewerRef = useRef(null);

  // 화살표 원본 위치(initialPosition) 영구 저장
  const initialPositionsRef = useRef(new Map());
  // 💡 메쉬/머티리얼의 원본 BaseColorFactor 및 AlphaMode 영구 저장
  const originalMaterialsRef = useRef(new Map());

  // 장비 작동 여부 확인
  const isBodyActive = latestEquipmentOp?.opStatus === 'ACTIVE';

  // 방향 추출 로직
  const getArrowDirections = () => {
    if (!isBodyActive || !latestEquipmentOp?.opStatusDetail) return [];

    const detail = latestEquipmentOp.opStatusDetail;

    if (Array.isArray(detail)) {
      return detail
        .map((d) => {
          const key = d.toString().trim().toUpperCase();
          return DIRECTION_MAP[key] || key.toLowerCase();
        })
        .filter(Boolean);
    }

    return detail
      .split(',')
      .map((item) => {
        const key = item.trim().toUpperCase();
        return DIRECTION_MAP[key] || key.toLowerCase();
      })
      .filter(Boolean);
  };

  const arrowDirections = getArrowDirections();
  const arrowDirectionsKey = arrowDirections.join(',');

  useEffect(() => {
    const modelViewer = viewerRef.current;
    if (!modelViewer) return;

    let animationFrameId;
    const startTime = performance.now();

    const setupAndAnimate = () => {
      const model = modelViewer.model;
      if (!model) return;

      const arrowDataList = [];
      const alertDataList = [];
      let sphereMaterial = null;

      // 1. 머티리얼 초기 원본 값 백업 및 기본 제어
      model.materials.forEach((material) => {
        if (!material || !material.pbrMetallicRoughness) return;

        const matName = material.name ? material.name.toLowerCase() : '';

        // 💡 원본 머티리얼 정보 최초 1회 백업
        if (!originalMaterialsRef.current.has(material.name)) {
          originalMaterialsRef.current.set(material.name, {
            baseColorFactor: [...material.pbrMetallicRoughness.baseColorFactor],
            alphaMode: material.getAlphaMode ? material.getAlphaMode() : 'OPAQUE',
          });
        }

        const origMat = originalMaterialsRef.current.get(material.name);

        // [alert 느낌표]
        if (matName.includes('alert')) {
          material.setAlphaMode('BLEND');
          if (showAlert) {
            alertDataList.push({ material });
          } else {
            material.pbrMetallicRoughness.setBaseColorFactor([0, 0, 0, 0]);
          }
        }
        // [exclamation 구체]
        else if (matName.includes('exclamation')) {
          sphereMaterial = material;
          material.setAlphaMode('BLEND');
          if (showExclamation) {
            const rgb = COLOR_MAP[alertType.toLowerCase()] || COLOR_MAP.alert;
            material.pbrMetallicRoughness.setBaseColorFactor([...rgb, 0.8]);
          } else {
            material.pbrMetallicRoughness.setBaseColorFactor([0, 0, 0, 0]);
          }
        }
        // [화살표 투명화 기본 적용]
        else if (matName.includes('arrow')) {
          material.setAlphaMode('BLEND');
          material.pbrMetallicRoughness.setBaseColorFactor([0, 0, 0, 0]);
        }
        // [일반 본체 메쉬 처리]
        else {
          if (isBodyActive) {
            // 💡 ACTIVE 상태: 저장된 원본 색상 및 알파 모드로 복구
            material.setAlphaMode(origMat.alphaMode);
            material.pbrMetallicRoughness.setBaseColorFactor(origMat.baseColorFactor);
          } else {
            // 💡 INACTIVE 상태: 반투명/회색조 적용
            material.setAlphaMode('BLEND');
            material.pbrMetallicRoughness.setBaseColorFactor([0.4, 0.4, 0.4, 0.3]);
          }
        }
      });

      // 2. Three.js Scene 노드 탐색 (화살표 및 바디 객체)
      const sceneSymbol = Object.getOwnPropertySymbols(modelViewer).find(
        (sym) => sym.description === 'scene' || sym.toString().includes('scene')
      );
      const scene = sceneSymbol ? modelViewer[sceneSymbol] : null;

      if (scene) {
        scene.traverse((object) => {
          if (!object) return;

          const name = object.name ? object.name.toLowerCase() : '';

          // body_object 복구/비활성화 명시적 처리
          if (name.includes('body_object') || name.includes('body')) {
            object.traverse((child) => {
              if (child.isMesh && child.material) {
                const targetMat = model.materials.find((m) => m.name === child.material.name);
                if (targetMat) {
                  const origMat = originalMaterialsRef.current.get(targetMat.name);
                  if (isBodyActive && origMat) {
                    targetMat.setAlphaMode(origMat.alphaMode);
                    targetMat.pbrMetallicRoughness.setBaseColorFactor(origMat.baseColorFactor);
                  } else if (!isBodyActive) {
                    targetMat.setAlphaMode('BLEND');
                    targetMat.pbrMetallicRoughness.setBaseColorFactor([0.4, 0.4, 0.4, 0.3]);
                  }
                }
              }
            });
          }

          // 화살표 노드 제어
          if (object.isMesh || object.isGroup) {
            let direction = null;
            if (name.includes('arrow_right')) direction = 'right';
            else if (name.includes('arrow_left')) direction = 'left';
            else if (name.includes('arrow_front')) direction = 'front';
            else if (name.includes('arrow_back')) direction = 'back';
            else if (name.includes('arrow_up')) direction = 'up';
            else if (name.includes('arrow_down')) direction = 'down';

            if (direction) {
              if (!initialPositionsRef.current.has(object.uuid)) {
                initialPositionsRef.current.set(object.uuid, object.position.clone());
              }
              const origPos = initialPositionsRef.current.get(object.uuid);
              object.position.copy(origPos);

              if (arrowDirections.includes(direction)) {
                const targetMat = model.materials.find(
                  (m) => m.name && (m.name.toLowerCase().includes(name) || name.includes(m.name.toLowerCase()))
                );

                if (targetMat) {
                  targetMat.setAlphaMode('BLEND');
                  arrowDataList.push({
                    node: object,
                    material: targetMat,
                    direction: direction,
                    initialPos: origPos.clone(),
                  });
                }
              }
            }
          }
        });
      }

      // 3. 루프 애니메이션
      const animate = (currentTime) => {
        const elapsed = (currentTime - startTime) / 1000;

        // [alert] 깜빡임 (0.35 ~ 1.0)
        if (showAlert && alertDataList.length > 0) {
          const rawSin = Math.sin((elapsed * 3) % (Math.PI * 2));
          const alertAlpha = 0.35 + ((rawSin + 1) / 2) * 0.65;
          const alertRgb = COLOR_MAP[alertType.toLowerCase()] || COLOR_MAP.alert;

          alertDataList.forEach(({ material }) => {
            if (material?.pbrMetallicRoughness) {
              material.pbrMetallicRoughness.setBaseColorFactor([
                alertRgb[0],
                alertRgb[1],
                alertRgb[2],
                alertAlpha,
              ]);
            }
          });
        }

        // [화살표] 이동 및 깜빡임
        const arrowSin = Math.sin((elapsed * 2.5) % (Math.PI * 2));
        const arrowAlpha = 0.4 + ((arrowSin + 1) / 2) * 0.6;
        const offset = ((arrowSin + 1) / 2) * 0.3;

        arrowDataList.forEach(({ node, material, direction, initialPos }) => {
          if (material?.pbrMetallicRoughness) {
            const arrowRgb = direction === 'up' ? [1, 0, 0] : [0, 0, 1];
            material.pbrMetallicRoughness.setBaseColorFactor([
              arrowRgb[0],
              arrowRgb[1],
              arrowRgb[2],
              arrowAlpha,
            ]);
          }

          if (node) {
            node.position.copy(initialPos);
            switch (direction) {
              case 'right': node.position.x += offset; break;
              case 'left': node.position.x -= offset; break;
              case 'front': node.position.z += offset; break;
              case 'back': node.position.z -= offset; break;
              case 'up': node.position.y += offset; break;
              case 'down': node.position.y -= offset; break;
              default: break;
            }
          }
        });

        // [exclamation] 맥동
        if (showExclamation && sphereMaterial?.pbrMetallicRoughness) {
          const pulseSin = Math.sin((elapsed * 3.5) % (Math.PI * 2));
          const sphereAlpha = 0.3 + ((pulseSin + 1) / 2) * 0.6;
          const currentColor = sphereMaterial.pbrMetallicRoughness.baseColorFactor;

          sphereMaterial.pbrMetallicRoughness.setBaseColorFactor([
            currentColor[0],
            currentColor[1],
            currentColor[2],
            sphereAlpha,
          ]);
        }

        animationFrameId = requestAnimationFrame(animate);
      };

      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(animate);
    };

    const handleLoad = () => {
      setupAndAnimate();
    };

    if (modelViewer.model) {
      setupAndAnimate();
    } else {
      modelViewer.addEventListener('load', handleLoad, { once: true });
    }

    return () => {
      modelViewer.removeEventListener('load', handleLoad);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [showAlert, alertType, showExclamation, isBodyActive, arrowDirectionsKey]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      <model-viewer
        ref={viewerRef}
        src={glbPath}
        ar
        camera-controls
        touch-action="pan-y"
        alt="A 3D model"
        camera-orbit="35deg 75deg 65%"
        field-of-view="25deg"
        reveal="auto"
        loading="eager"
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: 'transparent',
        }}
      ></model-viewer>
    </div>
  );
}

export default memo(ModelViewerCard);